import { DEFAULT_REGION, RegionCode } from '../../regions'
import { RegionConfig } from '../RemoteConfig'

/**
 * PricesRegion is a record of region codes to price values
 *
 * Don't access price[countryCode] directly
 *
 * Use PriceValue.get(price, region) to get the price value
 */
export type PricesRegion = Partial<Record<RegionCode, number>>

const getPriceValue = (price?: PricesRegion, region?: RegionConfig | null) => {
  if (!price) {
    return undefined
  }
  if (!region) {
    return undefined
  }
  // Use the region's own price first, fall back to priceCode (e.g. AT → DE)
  const ownPrice = price[region.countryCode]
  if (ownPrice != null) {
    return ownPrice
  }

  return region.priceCode ? price[region.priceCode] : undefined
}

const getSafeArrayPriceValue = (
  price: (PricesRegion | undefined)[],
  region?: RegionConfig | null
) => {
  // Look for actual price
  for (const p of price) {
    if (!p) {
      continue
    }
    const val = region ? getPriceValue(p, region) : p[DEFAULT_REGION]
    if (val) {
      return val
    }
  }

  return 0
}

const getSafePriceValue = (
  price?: PricesRegion | (PricesRegion | undefined)[],
  region?: RegionConfig | null
) => {
  if (!price) {
    return 0
  }

  if (Array.isArray(price)) {
    return getSafeArrayPriceValue(price, region)
  }

  if (!region) {
    return price[DEFAULT_REGION] ?? 0
  }

  return getPriceValue(price, region) ?? 0
}

/**
 * Utility class to get price values
 *
 * Don't access price[countryCode] directly
 */
export const PriceValue = {
  get: getPriceValue,
  safe: getSafePriceValue,
}
